/* eslint-disable react-refresh/only-export-components --
   Context module: the Provider component and its consumer hooks belong in one
   file by design. Fast-refresh's component-only-export rule doesn't apply. */
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

interface TenantCodeContextValue {
  tenantCode: string | null;
}

const TenantCodeContext = createContext<TenantCodeContextValue>({ tenantCode: null });

export function TenantCodeProvider({
  value,
  children,
}: {
  value: string | null;
  children: React.ReactNode;
}) {
  const ctx = useMemo(() => ({ tenantCode: value }), [value]);
  return (
    <TenantCodeContext.Provider value={ctx}>
      {children}
    </TenantCodeContext.Provider>
  );
}

/**
 * Returns the current tenant code from the URL prefix, or null for
 * sea-domain (non-prefixed) routes.
 */
export function useTenantCode(): string | null {
  return useContext(TenantCodeContext).tenantCode;
}

/**
 * Prepend the active tenant code to an absolute path.
 * Returns the path unchanged when no tenant code is active (sea domain).
 *
 * @example
 *   useTenantPath('/dashboard') // '/acme/dashboard' or '/dashboard'
 */
export function useTenantPath(path: string): string {
  const { tenantCode } = useContext(TenantCodeContext);
  if (!tenantCode) return path;
  return `/${tenantCode}${path}`;
}

/**
 * Hook variant that reads tenantCode from React Router params.
 * Use this inside route components matched by `/:tenantCode/*`.
 */
export function useTenantCodeFromParams(): string | undefined {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  return tenantCode;
}
